import {
  FC,
  useCallback,
  useEffect,
  useMemo, useRef,
  useState
} from 'react'
import {
  TagsSearchModalContainer,
  TagsSearchModalWrapper,
  ModalButton,
  ModalText,
  RingWrapper
} from './styles'
import { API, Card, PageModel } from '@brg/api'
import queryString from 'query-string'
import { useProfile } from '@brg/auth'
import { Colors, FlexBox } from '@brg/ui'
import { NoProjectWrapper } from "./styles";
import urlApi from 'shared/actions/urlApi'
import { useTranslation } from 'react-i18next'
import { Pagination } from 'shared/components/pagination'
import { ProjectSearchBarSorting } from 'shared/components/projectsSearchBar/components/projectSearchBarSorting'
import { CompleteTitle } from '../sendProjects/employStyles'
import { ProjectsCount } from 'shared/pages/ModeratePages/styles'
import { ShopCard } from 'shared/pages/Shop/ShopCard'
import { ProjectsGridNew } from 'shared/components/grids'
import { ModalSearchWrapper } from '../sendProjects/styles'
import MobileSearchBar from 'shared/components/MobileSearchBar/MobileSearchBar'
import { SelectRing } from 'shared/components/projectCard/projectCardNew'

export const useReproductionCards = (
  queryString: string | undefined,
  disabled?: boolean
) => {
  const [data, setData] = useState<PageModel<Card<'Global'>>>();
  const [loading, setLoading] = useState<any>(false)

  useEffect(() => {
    if (queryString && !disabled) {
      setLoading(true)
      urlApi()
        .getUserCollectionCards(queryString)
        .then((r) => {
          setData(r)
        })
        .finally(() => setLoading(false))
    }
  }, [queryString, disabled])

  return { data, loading }
}

interface ReproductionSelectModalProps {
  cardIds: any[]
  projectId: string
  onSubmit: (cards: any[]) => void
  multiple?: boolean
}

const ReproductionSelectModal: FC<ReproductionSelectModalProps> = ({
  onSubmit,
  cardIds,
  projectId,
  multiple = false
}) => {
  const { i18n } = useTranslation()
  const [selectedCards, setSelectedCards] = useState<any[]>(cardIds || [])
  const [sorting, setSorting] = useState(1)
  const profile = useProfile();
  const { t } = useTranslation();
  const buttonDisabled = !selectedCards?.length || (!multiple && selectedCards?.length !== 1)
  const [currentPage, setPage] = useState(1)
  const [searchText, setSearchText] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null)

  const scrollTop = () => {
    if (!scrollRef.current) return
    scrollRef.current?.scrollTo(0, 0)
  }

  const onClick = useCallback(
    (cardId: string, selected: boolean) => {

      if (selected) {
        setSelectedCards(selectedCards.filter((item) => item !== cardId))
      } else if (multiple) {
        setSelectedCards([...selectedCards, cardId])
      } else {
        setSelectedCards([cardId])
      }
    },
    [selectedCards, multiple])

  useEffect(() => {
    if (cardIds?.length) {
      setSelectedCards(cardIds)
    }
  }, [cardIds])

  const cardsQueryString = useMemo(() => {
    return queryString.stringify({
      cardOnlyImage: true,
      status: [2],
      isHeaderIgnored: true,
      isLite: 'true',
      isProduct: 'false',
      sort: sorting,
      page: currentPage,
      limit: 20,
      search: searchText,
      userIds: profile?.accountId,
      orderFirstCards: false,
      categoryCard: 0,
    })
  }, [sorting, projectId, profile, currentPage, searchText])

  const { data, loading } = useReproductionCards(cardsQueryString)

  const searchInput = useMemo(() => (
    <ModalSearchWrapper
      width={'100%'}
      offsetBottom={24}
      offsetBottomMobile={16}
    >
      <MobileSearchBar
        placeholder={t('project_edit.modal.reproduction.search_placeholder')}
        onCustomSearch={(data: string) => setSearchText(data)}
      />
    </ModalSearchWrapper>
  ), [t])

  const cards: Card[] = useMemo(() => {
    if (data?.data?.length) {
      return data?.data?.map((card) => API.toLang(card, i18n.language))
    }

    return []
  }, [data, i18n])

  const handleAttachClick = () => {
    if (!buttonDisabled) {
      onSubmit(selectedCards)
    }
  }

  return (
    <TagsSearchModalWrapper autoScroll>
      <TagsSearchModalContainer>
        <FlexBox width={'100%'} widthMobile={'100%'} style={{ position: 'relative' }} justify='space-between' justifyMobile='space-between' direction='row' directionMobile="row">
          <FlexBox width={'100%'} widthMobile={'100%'} justify='space-between' align='center' offsetBottom={48}>
            <CompleteTitle>
              {t('project_edit.modal.reproduction.main_title')}
            </CompleteTitle>
            <ModalButton
              onClick={handleAttachClick}
              fill={Colors.galBlack100}
              disabled={buttonDisabled}
              width={250}
            >
              {t('project_edit.modal.reproduction.add_button')}
            </ModalButton>
          </FlexBox>
        </FlexBox>
        <FlexBox width={'100%'}>
          {searchInput}
        </FlexBox>
        <FlexBox justify='space-between' offsetBottom={32}>
          <FlexBox align='center' direction='row' directionMobile='row' gx={24}>
            <ModalText>{t('project_edit.modal.reproduction.choosed_products')}</ModalText>
            <ProjectsCount>{selectedCards?.length}</ProjectsCount>
          </FlexBox>

          <ProjectSearchBarSorting
            selectedSorting={sorting}
            onSortingChange={setSorting}
            sortingMethods={[1, 20]}
          />
        </FlexBox>
        {cards?.length > 0 ? (
          <>
            <ProjectsGridNew k={20} isFixedHeight isLoading={loading} columns={4} mobileColumns={2}>
              {cards?.map((value: any) => {
                const selected = selectedCards.includes(value?.cardId)
                const hidePrice = value?.typeCard !== 'product';
                return (
                  <ShopCard
                    isModalGood
                    customClick={() => onClick(value?.cardId, selected)}
                    selected={selected}
                    key={value?.cardId}
                    card={value}
                    images={value?.images}
                    price={value?.priceInfo?.realProductPrice}
                    name={value?.name}
                    hidePrice={hidePrice}
                    selectedComponent={
                      <RingWrapper>
                        <SelectRing active={selected}>
                          <div>
                            {selected && (
                              <svg
                                width="12"
                                height="10"
                                viewBox="0 0 12 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1 4.85653L4.62216 8.45739L11.2699 1.83097L10.4389 1L4.62216 6.79545L1.80966
                                              4.02557L1 4.85653Z"
                                  fill="#FEFEFE"
                                  stroke="black"
                                />
                              </svg>
                            )}
                          </div>
                        </SelectRing>
                      </RingWrapper>
                    }
                  />
                )
              })
              }

            </ProjectsGridNew>
            <Pagination
              pages={data?.paging?.pages ?? 0}
              page={Number(currentPage)}
              onPageChange={(page: any) => {
                setPage(page)
                scrollTop()
              }}
              isCanLoadMore={currentPage < (data?.paging?.pages ?? 0)}
            />
          </>
        ) : (
          <NoProjectWrapper>{t("text.notfound")}</NoProjectWrapper>
        )}
      </TagsSearchModalContainer>
    </TagsSearchModalWrapper>
  )
}

export default ReproductionSelectModal
import {
  FC,
  useCallback,
  useEffect,
  useMemo, useRef,
  useState
} from 'react'
import {
  TagsSearchModalContainer,
  TagsSearchModalWrapper,
  ModalButton,
  ModalText,
  RingWrapper
} from './styles'
import { API, Card, PageModel } from '@brg/api'
import queryString from 'query-string'
import { useProfile } from '@brg/auth'
import { Colors, FlexBox } from '@brg/ui'
import { NoProjectWrapper } from "./styles";
import urlApi from 'shared/actions/urlApi'
import { useTranslation } from 'react-i18next'
import { Pagination } from 'shared/components/pagination'
import { ProjectSearchBarSorting } from 'shared/components/projectsSearchBar/components/projectSearchBarSorting'
import { CompleteTitle } from '../sendProjects/employStyles'
import { ProjectsCount } from 'shared/pages/ModeratePages/styles'
import { ShopCard } from 'shared/pages/Shop/ShopCard'
import { ProjectsGridNew } from 'shared/components/grids'
import { ModalSearchWrapper } from '../sendProjects/styles'
import MobileSearchBar from 'shared/components/MobileSearchBar/MobileSearchBar'
import { SelectRing } from 'shared/components/projectCard/projectCardNew'

export const useReproductionCards = (
  queryString: string | undefined,
  disabled?: boolean
) => {
  const [data, setData] = useState<PageModel<Card<'Global'>>>();
  const [loading, setLoading] = useState<any>(false)

  useEffect(() => {
    if (queryString && !disabled) {
      setLoading(true)
      urlApi()
        .getUserCollectionCards(queryString)
        .then((r) => {
          setData(r)
        })
        .finally(() => setLoading(false))
    }
  }, [queryString, disabled])

  return { data, loading }
}

interface ReproductionSelectModalProps {
  cardIds: any[]
  projectId: string
  onSubmit: (cards: any[]) => void
  multiple?: boolean
}

const ReproductionSelectModal: FC<ReproductionSelectModalProps> = ({
  onSubmit,
  cardIds,
  projectId,
  multiple = false
}) => {
  const { i18n } = useTranslation()
  const [selectedCards, setSelectedCards] = useState<any[]>(cardIds || [])
  const [sorting, setSorting] = useState(1)
  const profile = useProfile();
  const { t } = useTranslation();
  const buttonDisabled = !selectedCards?.length || (!multiple && selectedCards?.length !== 1)
  const [currentPage, setPage] = useState(1)
  const [searchText, setSearchText] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null)

  const scrollTop = () => {
    if (!scrollRef.current) return
    scrollRef.current?.scrollTo(0, 0)
  }

  const onClick = useCallback(
    (cardId: string, selected: boolean) => {

      if (selected) {
        setSelectedCards(selectedCards.filter((item) => item !== cardId))
      } else if (multiple) {
        setSelectedCards([...selectedCards, cardId])
      } else {
        setSelectedCards([cardId])
      }
    },
    [selectedCards, multiple])

  useEffect(() => {
    if (cardIds?.length) {
      setSelectedCards(cardIds)
    }
  }, [cardIds])

  const cardsQueryString = useMemo(() => {
    return queryString.stringify({
      cardOnlyImage: true,
      status: [2],
      isHeaderIgnored: true,
      isLite: 'true',
      isProduct: 'false',
      sort: sorting,
      page: currentPage,
      limit: 20,
      search: searchText,
      userIds: profile?.accountId,
      orderFirstCards: false,
      categoryCard: 0,
    })
  }, [sorting, projectId, profile, currentPage, searchText])

  const { data, loading } = useReproductionCards(cardsQueryString)

  const searchInput = useMemo(() => (
    <ModalSearchWrapper
      width={'100%'}
      offsetBottom={24}
      offsetBottomMobile={16}
    >
      <MobileSearchBar
        placeholder={t('project_edit.modal.reproduction.search_placeholder')}
        onCustomSearch={(data: string) => setSearchText(data)}
      />
    </ModalSearchWrapper>
  ), [t])

  const cards: Card[] = useMemo(() => {
    if (data?.data?.length) {
      return data?.data?.map((card) => API.toLang(card, i18n.language))
    }

    return []
  }, [data, i18n])

  const handleAttachClick = () => {
    if (!buttonDisabled) {
      onSubmit(selectedCards)
    }
  }

  return (
    <TagsSearchModalWrapper autoScroll>
      <TagsSearchModalContainer>
        <FlexBox width={'100%'} widthMobile={'100%'} style={{ position: 'relative' }} justify='space-between' justifyMobile='space-between' direction='row' directionMobile="row">
          <FlexBox width={'100%'} widthMobile={'100%'} justify='space-between' align='center' offsetBottom={48}>
            <CompleteTitle>
              {t('project_edit.modal.reproduction.main_title')}
            </CompleteTitle>
            <ModalButton
              onClick={handleAttachClick}
              fill={Colors.galBlack100}
              disabled={buttonDisabled}
              width={250}
            >
              {t('project_edit.modal.reproduction.add_button')}
            </ModalButton>
          </FlexBox>
        </FlexBox>
        <FlexBox width={'100%'}>
          {searchInput}
        </FlexBox>
        <FlexBox justify='space-between' offsetBottom={32}>
          <FlexBox align='center' direction='row' directionMobile='row' gx={24}>
            <ModalText>{t('project_edit.modal.reproduction.choosed_products')}</ModalText>
            <ProjectsCount>{selectedCards?.length}</ProjectsCount>
          </FlexBox>

          <ProjectSearchBarSorting
            selectedSorting={sorting}
            onSortingChange={setSorting}
            sortingMethods={[1, 20]}
          />
        </FlexBox>
        {cards?.length > 0 ? (
          <>
            <ProjectsGridNew k={20} isFixedHeight isLoading={loading} columns={4} mobileColumns={2}>
              {cards?.map((value: any) => {
                const selected = selectedCards.includes(value?.cardId)
                const hidePrice = value?.typeCard !== 'product';
                return (
                  <ShopCard
                    isModalGood
                    customClick={() => onClick(value?.cardId, selected)}
                    selected={selected}
                    key={value?.cardId}
                    card={value}
                    images={value?.images}
                    price={value?.priceInfo?.realProductPrice}
                    name={value?.name}
                    hidePrice={hidePrice}
                    selectedComponent={
                      <RingWrapper>
                        <SelectRing active={selected}>
                          <div>
                            {selected && (
                              <svg
                                width="12"
                                height="10"
                                viewBox="0 0 12 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1 4.85653L4.62216 8.45739L11.2699 1.83097L10.4389 1L4.62216 6.79545L1.80966
                                              4.02557L1 4.85653Z"
                                  fill="#FEFEFE"
                                  stroke="black"
                                />
                              </svg>
                            )}
                          </div>
                        </SelectRing>
                      </RingWrapper>
                    }
                  />
                )
              })
              }

            </ProjectsGridNew>
            <Pagination
              pages={data?.paging?.pages ?? 0}
              page={Number(currentPage)}
              onPageChange={(page: any) => {
                setPage(page)
                scrollTop()
              }}
              isCanLoadMore={currentPage < (data?.paging?.pages ?? 0)}
            />
          </>
        ) : (
          <NoProjectWrapper>{t("text.notfound")}</NoProjectWrapper>
        )}
      </TagsSearchModalContainer>
    </TagsSearchModalWrapper>
  )
}

export default ReproductionSelectModal
